import { useEffect, useRef } from 'react';
import styles from './BlackFridayModal.module.css';

/**
 * The designer's Lottie of the BLACK FRIDAY circles dropping into the foot of the offer card and
 * settling there (`black-friday-falling-circles.json`, a 673 × 527 canvas — the card's own size). It
 * plays ONCE and holds its last frame, which is the resting cluster the frame draws.
 *
 * The player and the 150 KB animation are a lazy chunk, fetched only when the modal opens: the modal
 * is mounted on every page for every reader and almost never shows. `onFail` hands the foot back to the
 * CSS cluster, so a failed chunk load still leaves the card looking finished rather than bare.
 */
export function BlackFridayFallingCircles({ onFail }: { onFail: () => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let anim: { destroy(): void } | undefined;

    Promise.all([import('lottie-web/build/player/lottie_light'), import('./black-friday-falling-circles.json')])
      .then(([player, data]) => {
        if (cancelled || !ref.current) return;
        const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const loaded = player.default.loadAnimation({
          container: ref.current,
          renderer: 'svg',
          loop: false,
          autoplay: !still,
          animationData: data.default,
        });
        if (still) loaded.goToAndStop(loaded.totalFrames - 1, true);
        anim = loaded;
      })
      .catch(() => {
        if (!cancelled) onFail();
      });

    return () => {
      cancelled = true;
      anim?.destroy();
    };
  }, [onFail]);

  return <div ref={ref} className={styles.fallingCircles} aria-hidden />;
}
